import { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import { Col, Divider, Checkbox, Tooltip } from 'antd'
import { Control, Controller, useFieldArray, useForm, useWatch, UseFormGetValues } from 'react-hook-form'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { PerProgressBar } from '../ProgressBar'
import { TargetModalType } from './types'
import { TargetsModalComment } from './TargetsModalComment'
import { useTargetsModalStore } from './TargetsModalStore'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import { TargetsModalCommentInput } from './TargetsModalCommentInput'
import { Country, ObjectiveInput, ObjectiveStatus } from '../../../types'
import './index.less'
import { TargetsModalAsterisk, TargetsModalMessage } from './TargetsModalMessageComponents'
import { TFunction } from 'i18next'
import RichTextBox from '../RichTextBox'

const Tasks = ({
  currentObjective,
  control,
  fields,
  getValues,
  publicProfile,
  t,
}: {
  currentObjective: ObjectiveInput
  control: Control<ObjectiveInput>
  fields: Record<'id', string>[]
  getValues: UseFormGetValues<ObjectiveInput>
  publicProfile?: boolean
  t: TFunction
}) => {
  useWatch({ control, name: 'keyResults' })

  const isDisabled =
    !!publicProfile ||
    [ObjectiveStatus.Closed, ObjectiveStatus.Dismissed, ObjectiveStatus.DismissedPending].includes(currentObjective.status)

  return (
    <PerFlexRow direction="column">
      <PerSpacer mb="xs">
        <PerProgressBar
          tasksDone={getValues().keyResults.filter(({ completed }) => completed).length}
          tasksAll={currentObjective.keyResults.length}
        />
      </PerSpacer>
      {fields.map((field, index) => {
        return (
          <PerFlexRow mb="s" key={field.id} className="fullWidth" align="middle" justify="start" noWrap>
            <Controller
              name={`keyResults.${index}.completed`}
              control={control}
              defaultValue={false}
              render={({ field: { onChange, value } }) => {
                return (
                  <PerFlexRow className="per-formikKeyResultsItem">
                    <Tooltip title={!isDisabled && t('targetModal.taskCheckboxTooltip')} placement="left">
                      <Checkbox checked={value} onChange={onChange} disabled={isDisabled}>
                        {getValues().keyResults[index].text}
                      </Checkbox>
                    </Tooltip>
                  </PerFlexRow>
                )
              }}
            />
          </PerFlexRow>
        )
      })}
    </PerFlexRow>
  )
}

export const TargetsModalFormViewTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { utcDateStringToMoment, formatDateShort } = useDateTime(user)

  const [currentObjective, currentComments, setNewTargetForm, targetModalType, currencyConversionRate, modalProps] = useTargetsModalStore(
    (state) => [
      state.currentObjective,
      state.currentComments,
      state.setNewTargetForm,
      state.targetModalType,
      state.currencyConversionRate,
      state.modalProps,
    ],
    shallow,
  )

  useEffect(() => {
    setNewTargetForm(newTargetForm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isDropZoneEnabled =
    !!currentObjective &&
    targetModalType === TargetModalType.viewTargetInMeeting &&
    currentObjective.status !== ObjectiveStatus.Closed &&
    currentObjective.status !== ObjectiveStatus.Dismissed &&
    currentObjective.status !== ObjectiveStatus.DismissedPending

  const hideCommentsHeader = !!currentObjective && currentObjective.status === ObjectiveStatus.Closed && !currentComments.length

  const newTargetForm = useForm<ObjectiveInput>({
    defaultValues: !!currentObjective ? currentObjective : {},
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const { control, getValues } = newTargetForm

  const { fields } = useFieldArray<ObjectiveInput>({
    name: 'keyResults',
    keyName: 'id',
    control,
  })

  const formatDate = (date: string) => {
    return formatDateShort(date, true) || '-'
  }

  const currencyTooltip: string | undefined =
    !!currentObjective &&
    !!currentObjective.currency &&
    !!modalProps.employeeCurrency &&
    currentObjective.currency !== modalProps.employeeCurrency
      ? currencyConversionRate !== undefined
        ? t('currencyConversion.tooltips.tableWarning')
        : t('currencyConversion.tooltips.tableConversionWarning', {
            startingCurrency: currentObjective.currency,
            targetCurrency: modalProps.employeeCurrency,
          })
      : undefined

  return !!currentObjective ? (
    <>
      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <PerText fontWeight="bold">{currentObjective.title}</PerText>
      </PerFlexRow>

      <PerFlexRow mt="xl">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>

      <Tasks
        currentObjective={currentObjective}
        control={control}
        fields={fields}
        getValues={getValues}
        publicProfile={modalProps.publicProfile}
        t={t}
      />

      {currentObjective.taskNotes && (
        <>
          <PerFlexRow mt="xl">
            <PerText appearance="tile-heading">{t('targetModal.notes')}</PerText>
          </PerFlexRow>
          <PerFlexRow direction="column" mt="m" mb="xxl">
            <RichTextBox text={currentObjective.taskNotes} readonly />
          </PerFlexRow>
        </>
      )}

      <PerFlexRow mt="l">
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.startDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.start)}</PerText>
        </Col>
        <Col span={1} />
        <Col span={8}>
          <PerText appearance="label-small" mb="xxs">
            {t('targetModal.dueDate')}
          </PerText>
          <PerText>{formatDate(currentObjective.due)}</PerText>
        </Col>
      </PerFlexRow>

      {!!currentObjective.targetAmount && modalProps.employeeCurrency && (
        <>
          <PerFlexRow mt="m">
            {!!currentObjective.achievedAmount && (
              <>
                <Col span={8}>
                  <Tooltip title={currencyTooltip} placement="left">
                    <PerText appearance="label-small" mb="xxs">
                      {t('targetModal.achievedAmount')}
                    </PerText>
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    <PerText fontWeight="bold">{formatCurrency(currentObjective.achievedAmount, currentObjective.currency!, true)}</PerText>
                    {currentObjective.currency !== modalProps.employeeCurrency && (
                      <div data-testid="converted-achieved-amount">
                        <PerText fontWeight="bold" mt="xxs" fontSize="s">
                          {calculateAndFormatNewTargetAmount(
                            currentObjective.achievedAmount,
                            modalProps.employeeCurrency,
                            currencyConversionRate,
                          )}
                        </PerText>
                      </div>
                    )}
                    <PerText mt="xxs">{((currentObjective.achievedAmount / currentObjective.targetAmount) * 100).toFixed(2) + '%'}</PerText>
                  </Tooltip>
                </Col>
                <Col span={1} />
              </>
            )}
            <Col span={8}>
              <Tooltip title={currencyTooltip} placement="left">
                <PerText appearance="label-small" mb="xxs">
                  {t('targetModal.targetAmount')}
                  {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && <TargetsModalAsterisk />}
                </PerText>
                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                <PerText fontWeight="bold">{formatCurrency(currentObjective.targetAmount, currentObjective.currency!, true)}</PerText>
                {currentObjective.currency !== modalProps.employeeCurrency && (
                  <div data-testid="converted-target-amount">
                    <PerText fontWeight="bold" fontSize="s" mt="xxs">
                      {calculateAndFormatNewTargetAmount(
                        currentObjective.targetAmount,
                        modalProps.employeeCurrency,
                        currencyConversionRate,
                      )}
                    </PerText>
                  </div>
                )}
              </Tooltip>
            </Col>
          </PerFlexRow>
          {modalProps.employeeCountry && modalProps.employeeCountry === Country.Pol && (
            <TargetsModalMessage message={t(`targetModal.targetMessage.${Country.Pol}`)} />
          )}
        </>
      )}

      <PerSpacer mt="m" />

      <TargetsModalAttachment showDropZone={isDropZoneEnabled} />

      {!hideCommentsHeader && (
        <>
          <Divider />
          <PerFlexRow>
            <PerText appearance="tile-heading">{t('targetModal.comments')}</PerText>
          </PerFlexRow>
        </>
      )}

      {currentComments
        .slice()
        .sort((a, b) => {
          const date1 = utcDateStringToMoment(a.date)
          const date2 = utcDateStringToMoment(b.date)
          return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
        })
        .map((note, index) => {
          return <TargetsModalComment comment={note} index={index} key={`${index}${note.date}`} />
        })}

      {!!currentObjective && currentObjective.status !== ObjectiveStatus.Closed && <TargetsModalCommentInput />}
    </>
  ) : null
}
